/**
 * Relationship Compass - Relationship Health Temperature Check
 *
 * This assessment provides couples with a comprehensive snapshot of their relationship
 * health across 8 key dimensions. It uses sliding scale ratings to gauge satisfaction
 * and identify areas of strength and growth.
 *
 * Design Philosophy:
 * - Non-judgmental focus on awareness and growth opportunities
 * - Individual completion first to prevent reactive responses
 * - Repeatable to track changes over time
 * - Actionable results that link to relevant modules and tools
 * - DDD brand voice: warm, research-grounded, aspirational but realistic
 */

const RelationshipHealthCheck = {
  id: 'RHC-1',
  title: "Relationship Health Temperature Check",
  subtitle: "A comprehensive snapshot of your relationship across 8 key dimensions",
  duration: "10-15 minutes",
  questionCount: 32, // 4 questions per dimension

  introduction: {
    heading: "Understanding Your Relationship Health",
    content: `This assessment helps you and your partner understand how your relationship is doing across the areas that matter most.

There are no "right" or "wrong" answers. We're not judging your relationship. We're helping you see it more clearly so you can celebrate strengths and identify opportunities for growth.

**How it works:**
- You'll rate various aspects of your relationship on a scale of 1-10
- Answer honestly based on how things actually are, not how you wish they were
- Your partner can complete this separately, then you can compare results
- You can retake this assessment over time to track your relationship's health

The whole thing takes about 10-15 minutes. Ready?`,
  },

  dimensions: {
    connectionIntimacy: {
      name: "Connection & Intimacy",
      description: "Emotional closeness, physical intimacy, feeling known and understood, quality time together",
      icon: "💕",
      color: "rose",
      module: 5, // Links to Module 5: Intimacy & Vulnerability
      questions: [0, 1, 2, 3]
    },

    communicationQuality: {
      name: "Communication Quality",
      description: "Ease of expressing feelings, active listening, handling difficult conversations, non-verbal communication",
      icon: "💬",
      color: "blue",
      module: 3, // Links to Module 3: Communication Patterns
      questions: [4, 5, 6, 7]
    },

    trustSafety: {
      name: "Trust & Safety",
      description: "Emotional safety, reliability and consistency, honesty and transparency, vulnerability comfort level",
      icon: "🛡️",
      color: "green",
      module: 6, // Links to Module 6: Trust & Repair
      questions: [8, 9, 10, 11]
    },

    conflictManagement: {
      name: "Conflict Management",
      description: "Disagreement resolution, repair after arguments, fair fighting practices, emotional regulation during conflict",
      icon: "⚖️",
      color: "amber",
      module: 4, // Links to Module 4: Conflict & Repair
      questions: [12, 13, 14, 15]
    },

    appreciationGratitude: {
      name: "Appreciation & Gratitude",
      description: "Feeling valued, expression of appreciation, recognition of efforts, celebration of each other",
      icon: "🙏",
      color: "purple",
      module: 2, // Links to Module 2: Recognition & Appreciation
      questions: [16, 17, 18, 19]
    },

    growthAlignment: {
      name: "Growth & Alignment",
      description: "Shared goals and vision, supporting individual growth, future planning alignment, evolving together vs. apart",
      icon: "🌱",
      color: "teal",
      module: 7, // Links to Module 7: Growing Together
      questions: [20, 21, 22, 23]
    },

    partnershipEquity: {
      name: "Partnership Equity",
      description: "Decision-making balance, division of responsibilities, financial transparency, power dynamics",
      icon: "⚖️",
      color: "indigo",
      module: 1, // Links to Module 1: Understanding Your Patterns
      questions: [24, 25, 26, 27]
    },

    funPlayfulness: {
      name: "Fun & Playfulness",
      description: "Shared laughter, enjoyment of time together, spontaneity, shared interests and activities",
      icon: "🎉",
      color: "pink",
      module: 8, // Links to Module 8: Relationship Resilience
      questions: [28, 29, 30, 31]
    }
  },

  questions: [
    // CONNECTION & INTIMACY (Questions 0-3)
    {
      id: 0,
      dimension: 'connectionIntimacy',
      type: "scale",
      text: "How emotionally close do you feel to your partner right now?",
      lowLabel: "Very distant",
      highLabel: "Deeply connected",
      reflectionPrompt: "What makes you feel most connected to your partner? What creates distance?"
    },

    {
      id: 1,
      dimension: 'connectionIntimacy',
      type: "scale",
      text: "How satisfied are you with the physical intimacy in your relationship?",
      lowLabel: "Very dissatisfied",
      highLabel: "Very satisfied",
      reflectionPrompt: "What would help you feel more satisfied with physical intimacy?"
    },

    {
      id: 2,
      dimension: 'connectionIntimacy',
      type: "scale",
      text: "How well does your partner truly know and understand you?",
      lowLabel: "Not well at all",
      highLabel: "Completely",
      reflectionPrompt: "What parts of yourself do you wish your partner understood better?"
    },

    {
      id: 3,
      dimension: 'connectionIntimacy',
      type: "scale",
      text: "How often do you spend quality, undistracted time together?",
      lowLabel: "Rarely",
      highLabel: "Very often",
      reflectionPrompt: "What would 'quality time' look like for you?"
    },

    // COMMUNICATION QUALITY (Questions 4-7)
    {
      id: 4,
      dimension: 'communicationQuality',
      type: "scale",
      text: "How easy is it for you to express your feelings to your partner?",
      lowLabel: "Very difficult",
      highLabel: "Very easy",
      reflectionPrompt: "What feelings are hardest to share? What makes them hard?"
    },

    {
      id: 5,
      dimension: 'communicationQuality',
      type: "scale",
      text: "How well does your partner listen when you're sharing something important?",
      lowLabel: "Not well",
      highLabel: "Extremely well",
      reflectionPrompt: "How do you know when your partner is truly listening?"
    },

    {
      id: 6,
      dimension: 'communicationQuality',
      type: "scale",
      text: "How effectively do you and your partner handle difficult conversations?",
      lowLabel: "Very poorly",
      highLabel: "Very effectively",
      reflectionPrompt: "What makes difficult conversations go well vs. poorly?"
    },

    {
      id: 7,
      dimension: 'communicationQuality',
      type: "scale",
      text: "How well do you understand each other's non-verbal communication?",
      lowLabel: "Not well",
      highLabel: "Very well",
      reflectionPrompt: "What non-verbal signals do you wish your partner noticed more?"
    },

    // TRUST & SAFETY (Questions 8-11)
    {
      id: 8,
      dimension: 'trustSafety',
      type: "scale",
      text: "How emotionally safe do you feel being vulnerable with your partner?",
      lowLabel: "Not safe",
      highLabel: "Completely safe",
      reflectionPrompt: "What would help you feel safer being vulnerable?"
    },

    {
      id: 9,
      dimension: 'trustSafety',
      type: "scale",
      text: "How reliable and consistent is your partner in following through?",
      lowLabel: "Very unreliable",
      highLabel: "Very reliable",
      reflectionPrompt: "When has your partner's reliability been tested?"
    },

    {
      id: 10,
      dimension: 'trustSafety',
      type: "scale",
      text: "How honest and transparent is your communication with each other?",
      lowLabel: "Often dishonest",
      highLabel: "Completely honest",
      reflectionPrompt: "Are there topics you avoid being fully honest about? Why?"
    },

    {
      id: 11,
      dimension: 'trustSafety',
      type: "scale",
      text: "How comfortable do you feel sharing your fears and insecurities?",
      lowLabel: "Very uncomfortable",
      highLabel: "Very comfortable",
      reflectionPrompt: "What fears or insecurities do you find hardest to share?"
    },

    // CONFLICT MANAGEMENT (Questions 12-15)
    {
      id: 12,
      dimension: 'conflictManagement',
      type: "scale",
      text: "How effectively do you resolve disagreements when they arise?",
      lowLabel: "Very ineffectively",
      highLabel: "Very effectively",
      reflectionPrompt: "What helps you resolve disagreements well?"
    },

    {
      id: 13,
      dimension: 'conflictManagement',
      type: "scale",
      text: "How well do you repair the relationship after arguments or conflicts?",
      lowLabel: "Very poorly",
      highLabel: "Very well",
      reflectionPrompt: "What does effective repair look like for you?"
    },

    {
      id: 14,
      dimension: 'conflictManagement',
      type: "scale",
      text: "How fairly do you fight when conflicts occur?",
      lowLabel: "Unfairly",
      highLabel: "Very fairly",
      reflectionPrompt: "What 'unfair' tactics show up in your conflicts?"
    },

    {
      id: 15,
      dimension: 'conflictManagement',
      type: "scale",
      text: "How well can you regulate your emotions during disagreements?",
      lowLabel: "Very poorly",
      highLabel: "Very well",
      reflectionPrompt: "What triggers make emotional regulation hardest?"
    },

    // APPRECIATION & GRATITUDE (Questions 16-19)
    {
      id: 16,
      dimension: 'appreciationGratitude',
      type: "scale",
      text: "How valued and appreciated do you feel by your partner?",
      lowLabel: "Not valued",
      highLabel: "Deeply valued",
      reflectionPrompt: "How do you most like to be shown appreciation?"
    },

    {
      id: 17,
      dimension: 'appreciationGratitude',
      type: "scale",
      text: "How often do you express appreciation for your partner?",
      lowLabel: "Rarely",
      highLabel: "Very often",
      reflectionPrompt: "What prevents you from expressing appreciation more?"
    },

    {
      id: 18,
      dimension: 'appreciationGratitude',
      type: "scale",
      text: "How well does your partner recognise your efforts and contributions?",
      lowLabel: "Not at all",
      highLabel: "Always",
      reflectionPrompt: "What efforts do you wish were noticed more?"
    },

    {
      id: 19,
      dimension: 'appreciationGratitude',
      type: "scale",
      text: "How often do you celebrate each other's wins and successes?",
      lowLabel: "Rarely",
      highLabel: "Always",
      reflectionPrompt: "How would you like to celebrate each other more?"
    },

    // GROWTH & ALIGNMENT (Questions 20-23)
    {
      id: 20,
      dimension: 'growthAlignment',
      type: "scale",
      text: "How aligned are you on your shared goals and vision for the future?",
      lowLabel: "Not aligned",
      highLabel: "Completely aligned",
      reflectionPrompt: "What future goals do you need to discuss more?"
    },

    {
      id: 21,
      dimension: 'growthAlignment',
      type: "scale",
      text: "How well does your partner support your individual growth and development?",
      lowLabel: "Not supportive",
      highLabel: "Very supportive",
      reflectionPrompt: "How could your partner better support your growth?"
    },

    {
      id: 22,
      dimension: 'growthAlignment',
      type: "scale",
      text: "How well are you planning and building your future together?",
      lowLabel: "Very poorly",
      highLabel: "Very well",
      reflectionPrompt: "What future plans need more discussion?"
    },

    {
      id: 23,
      dimension: 'growthAlignment',
      type: "scale",
      text: "Are you growing together or growing apart?",
      lowLabel: "Growing apart",
      highLabel: "Growing together",
      reflectionPrompt: "What makes you feel like you're growing together?"
    },

    // PARTNERSHIP EQUITY (Questions 24-27)
    {
      id: 24,
      dimension: 'partnershipEquity',
      type: "scale",
      text: "How balanced is decision-making in your relationship?",
      lowLabel: "Very unbalanced",
      highLabel: "Perfectly balanced",
      reflectionPrompt: "Who makes most of the decisions? How does that feel?"
    },

    {
      id: 25,
      dimension: 'partnershipEquity',
      type: "scale",
      text: "How fairly are responsibilities and tasks divided?",
      lowLabel: "Very unfairly",
      highLabel: "Very fairly",
      reflectionPrompt: "What responsibilities feel unbalanced?"
    },

    {
      id: 26,
      dimension: 'partnershipEquity',
      type: "scale",
      text: "How transparent are you about finances and money decisions?",
      lowLabel: "Not transparent",
      highLabel: "Completely transparent",
      reflectionPrompt: "What financial topics need more openness?"
    },

    {
      id: 27,
      dimension: 'partnershipEquity',
      type: "scale",
      text: "How healthy are the power dynamics in your relationship?",
      lowLabel: "Very unhealthy",
      highLabel: "Very healthy",
      reflectionPrompt: "Where do power imbalances show up most?"
    },

    // FUN & PLAYFULNESS (Questions 28-31)
    {
      id: 28,
      dimension: 'funPlayfulness',
      type: "scale",
      text: "How much do you laugh and have fun together?",
      lowLabel: "Rarely",
      highLabel: "All the time",
      reflectionPrompt: "When do you laugh most together?"
    },

    {
      id: 29,
      dimension: 'funPlayfulness',
      type: "scale",
      text: "How much do you genuinely enjoy spending time together?",
      lowLabel: "Not much",
      highLabel: "Absolutely",
      reflectionPrompt: "What activities do you enjoy most together?"
    },

    {
      id: 30,
      dimension: 'funPlayfulness',
      type: "scale",
      text: "How spontaneous and playful are you with each other?",
      lowLabel: "Not at all",
      highLabel: "Very",
      reflectionPrompt: "What would more spontaneity look like?"
    },

    {
      id: 31,
      dimension: 'funPlayfulness',
      type: "scale",
      text: "How many shared interests and activities do you have?",
      lowLabel: "Very few",
      highLabel: "Many",
      reflectionPrompt: "What new shared interests would you like to explore?"
    }
  ],

  // Scoring logic for scale-based assessments
  calculateScore: (responses) => {
    const dimensionScores = {};
    const dimensionCounts = {};

    // Initialize all dimensions
    Object.keys(RelationshipHealthCheck.dimensions).forEach(dim => {
      dimensionScores[dim] = 0;
      dimensionCounts[dim] = 0;
    });

    // Aggregate scores by dimension
    responses.forEach(response => {
      const question = RelationshipHealthCheck.questions[response.questionId];
      if (question && question.dimension) {
        dimensionScores[question.dimension] += response.value;
        dimensionCounts[question.dimension]++;
      }
    });

    // Calculate average score for each dimension (convert to 0-100)
    const normalizedScores = {};
    Object.keys(dimensionScores).forEach(dim => {
      if (dimensionCounts[dim] > 0) {
        const avgScore = dimensionScores[dim] / dimensionCounts[dim];
        normalizedScores[dim] = Math.round((avgScore / 10) * 100);
      } else {
        normalizedScores[dim] = 50;
      }
    });

    return normalizedScores;
  },

  // Determine overall relationship health level
  getHealthLevel: (avgScore) => {
    if (avgScore >= 80) return 'thriving';
    if (avgScore >= 65) return 'healthy';
    if (avgScore >= 50) return 'stable';
    if (avgScore >= 35) return 'struggling';
    return 'needsSupport';
  },

  healthLevels: {
    thriving: {
      name: "Thriving Relationship",
      tagline: "Your relationship is flourishing. Keep nurturing what works",
      description: "Your relationship is in excellent health across most dimensions. You're connected, communicating well, managing conflicts effectively, and growing together. The work you're doing is paying off. Keep investing in what's working, stay curious about each other, and continue the practices that got you here.",
      color: "green",
      icon: "🌟",
      strengths: [
        "Strong foundation across multiple dimensions",
        "Effective communication and conflict resolution",
        "Deep connection and mutual support",
        "Healthy patterns and positive dynamics"
      ],
      focus: [
        "Maintain the practices that are working",
        "Stay curious and keep growing together",
        "Don't take your connection for granted",
        "Share what you've learned with others"
      ]
    },

    healthy: {
      name: "Healthy Relationship",
      tagline: "Your relationship is doing well. Focus on growth areas",
      description: "Your relationship is in good health with some areas that could use attention. You've built a solid foundation, and there are clear opportunities to deepen your connection, improve specific patterns, or enhance particular dimensions. This is a great time for intentional growth.",
      color: "teal",
      icon: "💚",
      strengths: [
        "Solid foundation in key areas",
        "Willingness to work on the relationship",
        "Awareness of growth opportunities",
        "Commitment to improvement"
      ],
      focus: [
        "Identify 1-2 dimensions to focus on",
        "Build on existing strengths",
        "Practise new skills consistently",
        "Celebrate progress along the way"
      ]
    },

    stable: {
      name: "Stable Relationship",
      tagline: "Your relationship is steady. Invest in strengthening key areas",
      description: "Your relationship has stability but could benefit from more intentional work in several areas. You're not in crisis, but you're also not thriving. This is a pivotal moment: small, consistent investments now can prevent bigger issues later and help you build toward a more fulfilling connection.",
      color: "amber",
      icon: "🟡",
      strengths: [
        "Relationship has survived challenges",
        "Foundation exists to build upon",
        "Awareness that improvement is needed",
        "Opportunity for significant growth"
      ],
      focus: [
        "Address lower-scoring dimensions first",
        "Establish regular check-in practices",
        "Improve communication patterns",
        "Rebuild emotional connection"
      ]
    },

    struggling: {
      name: "Struggling Relationship",
      tagline: "Your relationship needs focussed attention. Small steps matter",
      description: "Your relationship is facing significant challenges across multiple dimensions. This doesn't mean it can't improve. It means you need intentional, focussed effort and possibly professional support. The good news is you're here seeking help. Small, consistent changes in key areas can create meaningful shifts.",
      color: "orange",
      icon: "🟠",
      strengths: [
        "You're seeking help and solutions",
        "Awareness of the challenges",
        "Potential for meaningful change",
        "Opportunity for transformation"
      ],
      focus: [
        "Start with one dimension at a time",
        "Consider professional support",
        "Focus on communication and safety",
        "Celebrate any small improvements"
      ]
    },

    needsSupport: {
      name: "Needs Support",
      tagline: "Your relationship needs professional help. Reach out now",
      description: "Your relationship is in significant distress across multiple dimensions. While this app can provide helpful tools and insights, we strongly recommend seeking professional support from a qualified couples therapist (or, if you feel unsafe with your partner, from a family violence service). The challenges you're facing are serious, and you deserve expert guidance to navigate them.",
      color: "red",
      icon: "🔴",
      strengths: [
        "You're taking the first step by seeking help",
        "Recognition that change is needed",
        "Courage to face difficult realities",
        "Potential for healing with proper support"
      ],
      focus: [
        "Seek professional support soon",
        "Focus on emotional and physical safety first. If you feel afraid of your partner, or they control, threaten or hurt you, contact a family violence service rather than couples therapy",
        "Use this app as a supplement to therapy",
        "Be patient with the healing process"
      ],
      resources: [
        "Find a qualified couples therapist through the Resources page (if there's no abuse)",
        "If you feel unsafe: a family violence service (1800RESPECT, 1800 737 732, in Australia; the Resources page lists services for each country this app serves) or your local emergency number",
        "Consider individual therapy alongside couples work",
        "Reach out to trusted friends or family for support"
      ]
    }
  },

  completionMessage: {
    heading: "Your Relationship Health Report is Ready",
    content: `We've mapped your relationship health across 8 key dimensions.

This isn't a judgement. It's a snapshot in time. A starting point for intentional growth and deeper connection.

Your relationship is unique. These results show where you are now, not where you'll always be. With awareness, intention, and practice, every dimension can improve.

Let's see what you discovered...`
  }
};

export default RelationshipHealthCheck;
